/**
 * Live-telemetry detail sections — the OTLP plugin's contributions to the
 * details panel (edge = a connection, module = a service). Each reads the
 * {@link ../liveStatsStore} for the selected element and renders the RED metrics
 * that are PRESENT (composable: a rate-only feed shows just the rate; a feed
 * with sampled traces adds error rate + latency percentiles). No stat at all →
 * the section renders nothing, so it self-hides when telemetry isn't flowing.
 *
 * Each metric is a labelled block: a caption (current value) over a visx trend
 * chart (rate/error area, latency p50–p95 band + p99 line). With <2 history
 * points the chart self-hides and only the caption shows.
 *
 * Pure views: state lives in the store; these only read + format.
 */
import type { ReactElement, ReactNode } from "react";
import type { DetailSelection } from "../../workbench/plugins/types.js";
import {
  useEdgeStat,
  useNodeStat,
  type LatencyStat,
} from "../liveStatsStore.js";
import { useEdgeHistory, useNodeHistory, type HistoryPoint } from "../liveHistoryStore.js";
import { useTraceLinks } from "./traceLinks.js";
import {
  MetricAreaChart,
  LatencyBandChart,
  CHART_COLORS,
  type LatencySample,
} from "./charts/index.js";

function fmtRate(perSec: number): string {
  if (perSec >= 100) return `${Math.round(perSec)} req/s`;
  if (perSec >= 10) return `${perSec.toFixed(1)} req/s`;
  if (perSec > 0) return `${perSec.toFixed(2)} req/s`;
  return "0 req/s";
}

function fmtMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  if (ms >= 100) return `${Math.round(ms)} ms`;
  return `${ms.toFixed(1)} ms`;
}

function fmtPct(fraction: number): string {
  const pct = fraction * 100;
  if (pct > 0 && pct < 0.1) return "<0.1%";
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
}

/** A labelled metric block: caption (label + current value) over its trend chart. */
function ChartBlock({
  label,
  value,
  tone,
  children,
}: {
  label: string;
  value: string;
  tone?: "error";
  children: ReactNode;
}): ReactElement {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
        <span
          className={"text-xs tabular-nums " + (tone === "error" ? "text-rose-300" : "text-slate-300")}
        >
          {value}
        </span>
      </div>
      {children}
    </div>
  );
}

/** The non-headline percentiles (p50 / p99) for latency when there's no trend to
 *  draw yet — the caption already shows p95, so this only adds what it doesn't. */
function LatencyStatic({ latencyMs }: { latencyMs?: LatencyStat }): ReactElement | null {
  if (!latencyMs) return null;
  const parts = [
    latencyMs.p50 !== undefined ? `p50 ${fmtMs(latencyMs.p50)}` : null,
    latencyMs.p99 !== undefined ? `p99 ${fmtMs(latencyMs.p99)}` : null,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <div className="text-[11px] tabular-nums text-slate-400">{parts.join(" · ")}</div>;
}

function TelemetryPanel({
  perSec,
  errorRate,
  latencyMs,
  sampled,
  history,
  traceUrl,
  traceLabel,
}: {
  perSec: number;
  errorRate?: number;
  latencyMs?: LatencyStat;
  sampled?: number;
  history?: readonly HistoryPoint[];
  traceUrl?: string | null;
  traceLabel?: string;
}): ReactElement {
  const h = history ?? [];
  const enough = h.length >= 2;
  const hasErr = errorRate !== undefined || h.some((p) => p.errorRate !== undefined);
  const hasLat = latencyMs !== undefined || h.some((p) => p.p95 !== undefined);
  const rateSeries = h.map((p) => p.perSec);
  const errSeries = h.map((p) => (p.errorRate ?? 0) * 100);
  const latSeries: LatencySample[] = h.map((p) => ({ p50: p.p50, p95: p.p95, p99: p.p99 }));
  const latHead = latencyMs?.p95 !== undefined ? `p95 ${fmtMs(latencyMs.p95)}` : "—";
  return (
    <div data-telemetry-section>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
        <span
          aria-hidden
          className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
        />
        Live telemetry
      </div>
      <div className="space-y-3">
        <ChartBlock label="Rate" value={fmtRate(perSec)}>
          {enough && (
            <MetricAreaChart
              values={rateSeries}
              color={CHART_COLORS.rate}
              format={fmtRate}
              label="Rate"
            />
          )}
        </ChartBlock>
        {hasErr && (
          <ChartBlock
            label="Error rate"
            value={fmtPct(errorRate ?? 0)}
            tone={(errorRate ?? 0) > 0 ? "error" : undefined}
          >
            {enough && (
              <MetricAreaChart
                values={errSeries}
                color={CHART_COLORS.errors}
                format={(v) => fmtPct(v / 100)}
                label="Error rate"
              />
            )}
          </ChartBlock>
        )}
        {hasLat && (
          <ChartBlock label="Latency" value={latHead}>
            {enough ? (
              <LatencyBandChart points={latSeries} format={fmtMs} />
            ) : (
              <LatencyStatic latencyMs={latencyMs} />
            )}
          </ChartBlock>
        )}
      </div>
      {sampled !== undefined && (
        <div className="mt-2 text-[10px] text-slate-500">
          errors/latency over {sampled} sampled {sampled === 1 ? "trace" : "traces"}
        </div>
      )}
      {traceUrl && (
        <a
          href={traceUrl}
          target="_blank"
          rel="noreferrer"
          data-trace-link
          className="mt-2 inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300"
        >
          View in {traceLabel ?? "traces"} ↗
        </a>
      )}
    </div>
  );
}

/** Edge (connection) telemetry — RED for the selected directed service pair. */
export function EdgeTelemetrySection({ selection }: { selection: DetailSelection }): ReactElement | null {
  const edge = selection.kind === "edge" ? selection : null;
  // Hooks called unconditionally (rules-of-hooks); empty keys resolve to nothing.
  const stat = useEdgeStat(edge?.from ?? "", edge?.to ?? "");
  const history = useEdgeHistory(edge?.from ?? "", edge?.to ?? "");
  const links = useTraceLinks();
  if (!edge || !stat) return null;
  return (
    <TelemetryPanel
      perSec={stat.perSec}
      errorRate={stat.errorRate}
      latencyMs={stat.latencyMs}
      sampled={stat.sampled}
      history={history}
      traceUrl={links?.edgeUrl(edge.from, edge.to)}
      traceLabel={links?.label}
    />
  );
}

/** Module (service) telemetry — inbound RED for the selected service. */
export function ModuleTelemetrySection({ selection }: { selection: DetailSelection }): ReactElement | null {
  const mod = selection.kind === "module" ? selection : null;
  const stat = useNodeStat(mod?.moduleId ?? "");
  const history = useNodeHistory(mod?.moduleId ?? "");
  const links = useTraceLinks();
  if (!mod || !stat) return null;
  return (
    <TelemetryPanel
      perSec={stat.perSec}
      errorRate={stat.errorRate}
      latencyMs={stat.latencyMs}
      sampled={stat.sampled}
      history={history}
      traceUrl={links?.nodeUrl(mod.moduleId)}
      traceLabel={links?.label}
    />
  );
}
